import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSound } from "@/context/SoundContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import {
  Package, Warehouse, MonitorCheck, ShieldCheck, LayoutDashboard, MapPin, Users,
  Settings, BarChart3, Volume2, VolumeX, LogOut, Radio, Gauge, ScanLine, Activity, KeyRound, Building2,
  LifeBuoy, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const NAV = {
  warehouse_agent: [
    { to: "/warehouse", label: "Warehouse", icon: Warehouse },
    { to: "/status", label: "Live Status", icon: Activity },
    { to: "/coaching", label: "My Scorecard", icon: Gauge },
  ],
  front_desk_agent: [
    { to: "/frontdesk", label: "Front Desk", icon: MonitorCheck },
    { to: "/checkin", label: "Check-In Desk", icon: ScanLine },
    { to: "/status", label: "Live Status", icon: Activity },
    { to: "/coaching", label: "My Scorecard", icon: Gauge },
  ],
  supervisor: [
    { to: "/supervisor", label: "Supervisor", icon: ShieldCheck },
    { to: "/checkin", label: "Check-In Desk", icon: ScanLine },
    { to: "/status", label: "Live Status", icon: Activity },
    { to: "/warehouse", label: "Warehouse", icon: Warehouse },
    { to: "/frontdesk", label: "Front Desk", icon: MonitorCheck },
    { to: "/coaching", label: "Coaching", icon: Gauge },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  ],
  company_admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/locations", label: "Locations", icon: MapPin },
    { to: "/admin/employees", label: "Employees", icon: Users },
    { to: "/checkin", label: "Check-In Desk", icon: ScanLine },
    { to: "/status", label: "Live Status", icon: Activity },
    { to: "/supervisor", label: "Live Ops", icon: ShieldCheck },
    { to: "/coaching", label: "Coaching", icon: Gauge },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ],
  platform_admin: [
    { to: "/platform", label: "Companies", icon: Building2 },
  ],
};

export function DashboardLayout({ children, title, needsStore = true }) {
  const { user, logout, stores, currentStore, setCurrentStore, impersonating, stopImpersonation } = useAuth();
  const sound = useSound();
  const navigate = useNavigate();
  const nav = NAV[user?.role] || [];
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpForm, setHelpForm] = useState({ subject: "", message: "" });
  const [helpBusy, setHelpBusy] = useState(false);

  const submitHelp = async () => {
    if (!helpForm.subject.trim() || !helpForm.message.trim()) { toast.error("Please add a subject and a message"); return; }
    setHelpBusy(true);
    try {
      await api.post("/support/tickets", helpForm);
      toast.success("Support request sent. We'll get back to you shortly.");
      setHelpOpen(false);
      setHelpForm({ subject: "", message: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setHelpBusy(false); }
  };

  const exitImpersonation = async () => {
    await stopImpersonation();
    navigate("/platform");
  };

  const changePassword = async () => {
    if (pwForm.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password updated");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPwBusy(false);
    }
  };

  useEffect(() => {
    if (needsStore && !currentStore && stores.length > 1) navigate("/select-store");
  }, [needsStore, currentStore, stores, navigate]);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 border-r border-border bg-[#0d0d0d] flex flex-col sticky top-0 h-screen">
        <div className="px-4 py-5 border-b border-border">
          <div className="bg-white rounded-md px-3 py-2.5 flex items-center justify-center">
            <img src="/trackboard-logo.png" alt="Trackboard by LOM Group JA" className="w-full h-auto" data-testid="sidebar-logo" />
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/admin"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors duration-200 rounded-sm ${
                  isActive ? "bg-white text-black" : "text-muted-foreground hover:text-white hover:bg-secondary"
                }`}>
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <div className="text-sm font-semibold truncate">{user?.full_name}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{user?.role?.replace(/_/g, " ")}</div>
          <Button variant="ghost" size="sm" className="w-full mt-3 justify-start gap-2 px-2"
            onClick={() => setPwOpen(true)} data-testid="change-password-btn">
            <KeyRound size={14} /> Change Password
          </Button>
          {user?.role !== "platform_admin" && (
            <Button variant="ghost" size="sm" className="w-full mt-1 justify-start gap-2 px-2"
              onClick={() => setHelpOpen(true)} data-testid="get-help-btn">
              <LifeBuoy size={14} /> Get Help
            </Button>
          )}
          <Button variant="outline" size="sm" className="w-full mt-1 justify-start gap-2"
            onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
            <LogOut size={14} /> Sign Out
          </Button>
        </div>

        <Dialog open={pwOpen} onOpenChange={setPwOpen}>
          <DialogContent className="bg-card border-border" data-testid="change-password-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Change Password</DialogTitle>
              <DialogDescription>Enter your current password and choose a new one (at least 8 characters).</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input type="password" placeholder="Current password" value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="pw-current" />
              <Input type="password" placeholder="New password" value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="pw-new" />
              <Input type="password" placeholder="Confirm new password" value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && changePassword()} data-testid="pw-confirm" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
              <Button onClick={changePassword} disabled={pwBusy} data-testid="pw-save-btn">{pwBusy ? "Saving…" : "Update Password"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </aside>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="bg-card border-border" data-testid="get-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Contact Support</DialogTitle>
            <DialogDescription>Describe your issue and the Trackboard team will get back to you by email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Subject" value={helpForm.subject}
              onChange={(e) => setHelpForm({ ...helpForm, subject: e.target.value })} data-testid="help-subject" />
            <Textarea placeholder="Describe your issue…" rows={5} value={helpForm.message}
              onChange={(e) => setHelpForm({ ...helpForm, message: e.target.value })} data-testid="help-message" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHelpOpen(false)}>Cancel</Button>
            <Button onClick={submitHelp} disabled={helpBusy} data-testid="help-send-btn">{helpBusy ? "Sending…" : "Send Request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {impersonating && (
          <div className="bg-alert-amber text-black px-6 py-2 flex items-center gap-3 text-sm font-semibold" data-testid="impersonation-banner">
            <LifeBuoy size={16} />
            <span>Support mode — you are viewing this customer's account as {user?.full_name}.</span>
            <Button size="sm" variant="secondary" className="ml-auto h-7 gap-1" onClick={exitImpersonation} data-testid="exit-impersonation-btn">
              <ArrowLeft size={13} /> Return to Platform Owner
            </Button>
          </div>
        )}
        <header className="sticky top-0 z-20 bg-[#0d0d0d]/95 backdrop-blur border-b border-border px-6 py-3 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight leading-none">{title}</h1>
            {currentStore && (
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin size={12} /> {currentStore.store_name} · {currentStore.store_code}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {stores.length > 1 && (
              <Select value={currentStore?.id || ""} onValueChange={(id) => setCurrentStore(stores.find((s) => s.id === id))}>
                <SelectTrigger className="w-52" data-testid="store-switcher"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>
                  {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {/* Sound control */}
            {sound.enabled ? (
              <div className="flex items-center gap-2 bg-secondary px-3 py-1.5 rounded-sm">
                <Volume2 size={16} className="text-alert-green" />
                <Slider className="w-20" min={0} max={1} step={0.1} value={[sound.volume]}
                  onValueChange={(v) => sound.setVolume(v[0])} data-testid="volume-slider" />
                <button onClick={() => sound.testSound()} className="text-xs text-muted-foreground hover:text-white" data-testid="test-sound-btn">Test</button>
                <button onClick={() => { sound.mute(); toast("Sound muted"); }} data-testid="mute-btn"><VolumeX size={16} className="text-muted-foreground hover:text-white" /></button>
              </div>
            ) : (
              <Button size="sm" variant="destructive" className="gap-2" onClick={() => { sound.enable(); toast.success("Sound alerts enabled"); }} data-testid="enable-sound-btn">
                <Radio size={14} /> Enable Sound Alerts
              </Button>
            )}
          </div>
        </header>
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}

export default DashboardLayout;
